import React, { useState, useEffect } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  InputAdornment,
  Pagination,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import DescriptionIcon from "@mui/icons-material/Description";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";

import { api } from "@api/api";
import { ajustarTamanhoFonteEscola } from "../../../estilos/global";
import TransicaoPagina from "../../../componentes/comuns/TransicaoPagina";

const ITEMS_PER_PAGE = 6;

const formatSeiNumber = (seiNumber) => {
  if (!seiNumber) return "";

  const cleaned = seiNumber.replace(/\D/g, "");
  let formatted = "";

  if (cleaned.length > 0) formatted += cleaned.substring(0, 4);
  if (cleaned.length > 4) formatted += "." + cleaned.substring(4, 6);
  if (cleaned.length > 6) formatted += "." + cleaned.substring(6, 13);
  if (cleaned.length > 13) formatted += "/" + cleaned.substring(13, 17);
  if (cleaned.length > 17) formatted += "-" + cleaned.substring(17, 19);

  return formatted;
};

const StatusTag = ({ type, count }) => {
  const config = {
    ativa: {
      icon: <EditIcon fontSize="small" />,
      label: `${count} correção(s)`,
      color: "error",
      tooltip: `${count} documento(s) aberto(s) para edição`,
      bgColor: "#ffebee",
      textColor: "#c62828",
      borderColor: "#ef9a9a",
    },
    inativa: {
      icon: <HourglassTopIcon fontSize="small" />,
      label: `${count} em análise`,
      color: "warning",
      tooltip: `${count} documento(s) em análise`,
      bgColor: "#fff8e1",
      textColor: "#ff8f00",
      borderColor: "#ffe0b2",
    },
    aprovado: {
      icon: <CheckIcon fontSize="small" />,
      label: `${count} aprovado(s)`,
      color: "success",
      tooltip: `${count} documento(s) aprovados`,
      bgColor: "#e8f5e9",
      textColor: "#2e7d32",
      borderColor: "#a5d6a7",
    },
    total: {
      icon: <DescriptionIcon fontSize="small" />,
      label: `${count} documento(s)`,
      color: "info",
      tooltip: `Total de ${count} documento(s) neste processo`,
      bgColor: "#e3f2fd",
      textColor: "#1565c0",
      borderColor: "#90caf9",
    },
  };

  const { icon, label, tooltip, bgColor, textColor, borderColor } =
    config[type];

  return (
    <Tooltip title={tooltip} arrow>
      <Chip
        icon={icon}
        label={label}
        size="small"
        sx={{
          height: 24,
          backgroundColor: bgColor,
          color: textColor,
          border: `1px solid ${borderColor}`,
          fontSize: ajustarTamanhoFonteEscola("0.75rem"),
          fontWeight: 600,
          "& .MuiChip-icon": {
            color: textColor,
            fontSize: 16,
            marginLeft: "6px",
          },
          "& .MuiChip-label": {
            paddingLeft: "5px",
            paddingRight: "7px",
          },
        }}
      />
    </Tooltip>
  );
};

const ProcessCard = ({ process, onClick }) => {
  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      sx={{
        minHeight: 104,
        padding: { xs: 1.5, md: 1.75 },
        display: "flex",
        alignItems: { xs: "flex-start", md: "center" },
        flexDirection: { xs: "column", md: "row" },
        gap: 1.5,
        border: "1px solid #9fcdff",
        borderRadius: 2,
        backgroundColor: "#ffffff",
        boxShadow: "none",
        cursor: "pointer",
        transition: "border-color 0.2s, background-color 0.2s",
        "&:hover": {
          borderColor: "#087cf0",
          backgroundColor: "#f8fbff",
        },
      }}
    >
      <Avatar
        sx={{
          width: 48,
          height: 48,
          flexShrink: 0,
          color: "#087cf0",
          backgroundColor: "#eaf4ff",
        }}
      >
        <DescriptionIcon />
      </Avatar>

      <Divider
        orientation="vertical"
        flexItem
        sx={{
          display: { xs: "none", md: "block" },
          borderColor: "#c9def4",
        }}
      />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body1"
          sx={{
            color: "#0767d8",
            fontWeight: 700,
            overflowWrap: "anywhere",
          }}
        >
          {process.processtype}
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            columnGap: 2,
            rowGap: 0.5,
            marginTop: 0.5,
            color: "#4f5f85",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <SchoolRoundedIcon sx={{ fontSize: 18 }} />
            <Typography variant="body2">
              {process.schoolname || "Não informado"}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <LocationOnOutlinedIcon sx={{ fontSize: 18 }} />
            <Typography variant="body2">
              {process.state || "Não informado"}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <CalendarMonthOutlinedIcon sx={{ fontSize: 18 }} />
            <Typography variant="body2">
              {new Date(process.startdate).toLocaleDateString()}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <DescriptionIcon sx={{ fontSize: 18 }} />
            <Typography variant="body2">
              SEI: {formatSeiNumber(process.seinumber) || "Não informado"}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 0.75,
            marginTop: 1,
          }}
        >
          {process.totalDocumentos > 0 && (
            <StatusTag type="total" count={process.totalDocumentos} />
          )}

          {process.aprovacao > 0 && (
            <StatusTag type="aprovado" count={process.aprovacao} />
          )}

          {process.alteracoes_ativas > 0 && (
            <StatusTag type="ativa" count={process.alteracoes_ativas} />
          )}

          {process.alteracoes_inativas > 0 && (
            <StatusTag type="inativa" count={process.alteracoes_inativas} />
          )}
        </Box>
      </Box>

      <Button
        variant="outlined"
        onClick={(event) => {
          event.stopPropagation();
          onClick();
        }}
        sx={{
          minWidth: 130,
          minHeight: 38,
          flexShrink: 0,
          color: "#0767d8",
          borderColor: "#087cf0",
          backgroundColor: "#ffffff",
          textTransform: "none",
          boxShadow: "none",
          "&:hover": {
            color: "#ffffff",
            borderColor: "#087cf0",
            backgroundColor: "#087cf0",
            boxShadow: "none",
          },
        }}
      >
        Ver detalhes
      </Button>
    </Paper>
  );
};

const ControlesPaginacao = ({
  page,
  totalPages,
  totalProcesses,
  primeiroProcesso,
  ultimoProcesso,
  onChange,
}) => {
  if (totalProcesses === 0) return null;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        flexWrap: "wrap",
        gap: 1.5,
      }}
    >
      <Typography
        variant="body2"
        sx={{ color: "#5f6f94", whiteSpace: "nowrap" }}
      >
        Exibindo {primeiroProcesso}–{ultimoProcesso} de {totalProcesses}
      </Typography>

      <Pagination
        count={Math.max(totalPages, 1)}
        page={page}
        onChange={onChange}
        siblingCount={1}
        boundaryCount={1}
        sx={{
          "& .MuiPaginationItem-root": {
            color: "#33446b",
            borderRadius: 1.5,
          },
          "& .MuiPaginationItem-root.Mui-selected": {
            color: "#ffffff",
            backgroundColor: "#087cf0",
            "&:hover": { backgroundColor: "#0767d8" },
          },
        }}
      />
    </Box>
  );
};

export default function PaginaProcessosEscola() {
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    async function fetchProcesses() {
      try {
        const token = localStorage.getItem("token");

        const response = await fetch(api.listProcessTypes(), {
          method: "GET",
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
            "Content-Type": "application/json",
          },
          credentials: "include",
        });

        if (!response.ok) {
          throw new Error(`Erro na API: ${response.status}`);
        }

        const processesData = await response.json();

        const activeProcesses = processesData
          .filter((process) => !process.portarianumber)
          .sort(
            (processA, processB) =>
              new Date(processA.startdate) - new Date(processB.startdate),
          );

        setProcesses(
          activeProcesses.map((process) => ({
            ...process,
            formattedSeiNumber: formatSeiNumber(process.seinumber),
          })),
        );
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    }

    fetchProcesses();
  }, []);

  const handleCardClick = (process) => {
    navigate(`/detalhes-processo/${process.id}/${process.schoolname}`);
  };

  const filteredProcesses = processes.filter(
    (process) =>
      process.processtype.toLowerCase().includes(searchTerm.toLowerCase()) ||
      process.schoolname.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (process.seinumber &&
        process.seinumber.toLowerCase().includes(searchTerm.toLowerCase())),
  );

  const handleChangePage = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const totalProcesses = filteredProcesses.length;
  const totalPages = Math.ceil(totalProcesses / ITEMS_PER_PAGE);

  const primeiroProcesso =
    totalProcesses === 0 ? 0 : (page - 1) * ITEMS_PER_PAGE + 1;

  const ultimoProcesso = Math.min(page * ITEMS_PER_PAGE, totalProcesses);

  const paginatedProcesses = filteredProcesses.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE,
  );

  return (
    <TransicaoPagina variante="navegacao">
      <Box sx={{ minHeight: 0, paddingY: 0, backgroundColor: "#ffffff" }}>
        <div>
          <Container maxWidth={false} disableGutters>
            <Box>
              <Grid
                container
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
              >
                <Grid item xs={12}>
                  <div>
                    <Typography
                      variant="h4"
                      component="h1"
                      sx={{
                        color: "#0767d8",
                        fontWeight: 700,
                        marginBottom: 0.5,
                      }}
                    >
                      Processos das Escolas
                    </Typography>

                    <Typography variant="body1" sx={{ color: "#5f6f94" }}>
                      Gerencie e acompanhe todos os processos educacionais
                    </Typography>
                  </div>
                </Grid>

                <Grid item xs={12}>
                  <div style={{ width: "100%" }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: { xs: "stretch", md: "center" },
                        justifyContent: "space-between",
                        flexDirection: { xs: "column", md: "row" },
                        gap: 2,
                      }}
                    >
                      <TextField
                        variant="outlined"
                        placeholder="Pesquisar por processo, escola ou SEI..."
                        value={searchTerm}
                        onChange={(event) => {
                          setSearchTerm(event.target.value);
                          setPage(1);
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ color: "#5f6f94" }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          width: { xs: "100%", md: 440 },
                          "& .MuiOutlinedInput-root": {
                            minHeight: 48,
                            borderRadius: 2,
                            backgroundColor: "#ffffff",
                            "& fieldset": { borderColor: "#9fcdff" },
                            "&:hover fieldset": { borderColor: "#087cf0" },
                            "&.Mui-focused fieldset": {
                              borderColor: "#087cf0",
                            },
                          },
                        }}
                      />

                      <ControlesPaginacao
                        page={page}
                        totalPages={totalPages}
                        totalProcesses={totalProcesses}
                        primeiroProcesso={primeiroProcesso}
                        ultimoProcesso={ultimoProcesso}
                        onChange={handleChangePage}
                      />
                    </Box>
                  </div>
                </Grid>
              </Grid>

              <Paper
                variant="outlined"
                sx={{
                  marginTop: 3,
                  padding: { xs: 2, md: 2.5 },
                  border: "1px solid #9fcdff",
                  borderRadius: 2,
                  backgroundColor: "#ffffff",
                  boxShadow: "none",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    marginBottom: 1.5,
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{ color: "#0767d8", fontWeight: 700 }}
                  >
                    Processos cadastrados
                  </Typography>

                  <Chip
                    label={totalProcesses}
                    size="small"
                    sx={{
                      color: "#0767d8",
                      backgroundColor: "#eaf4ff",
                      fontWeight: 700,
                    }}
                  />
                </Box>

                {loading ? (
                  <div>
                    <Stack
                      alignItems="center"
                      justifyContent="center"
                      sx={{ height: "300px" }}
                    >
                      <CircularProgress
                        size={60}
                        thickness={4}
                        color="primary"
                      />

                      <Typography
                        variant="body1"
                        sx={{ mt: 3, color: "primary.main" }}
                      >
                        Carregando processos...
                      </Typography>
                    </Stack>
                  </div>
                ) : error ? (
                  <div>
                    <Paper
                      elevation={2}
                      sx={{
                        p: 3,
                        mt: 3,
                        backgroundColor: "error.light",
                      }}
                    >
                      <Typography
                        variant="h6"
                        color="error.main"
                        align="center"
                      >
                        Ocorreu um erro
                      </Typography>

                      <Typography
                        variant="body1"
                        color="error.main"
                        align="center"
                        sx={{ mt: 1 }}
                      >
                        {error}
                      </Typography>
                    </Paper>
                  </div>
                ) : (
                  <>
                    <div>
                      <Grid container spacing={1} sx={{ mt: 2 }}>
                        {paginatedProcesses.length === 0 ? (
                          <Grid item xs={12}>
                            <div>
                              <Paper
                                elevation={0}
                                sx={{ p: 4, textAlign: "center" }}
                              >
                                <Typography variant="h6" color="textSecondary">
                                  Nenhum processo encontrado
                                </Typography>

                                <Typography variant="body1" sx={{ mt: 1 }}>
                                  Tente ajustar sua pesquisa ou verifique os
                                  filtros
                                </Typography>
                              </Paper>
                            </div>
                          </Grid>
                        ) : (
                          paginatedProcesses.map((process) => (
                            <Grid item xs={12} key={process.id}>
                              <div>
                                <ProcessCard
                                  process={process}
                                  onClick={() => handleCardClick(process)}
                                />
                              </div>
                            </Grid>
                          ))
                        )}
                      </Grid>
                    </div>

                    {totalProcesses > 0 && (
                      <div>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            marginTop: 2,
                          }}
                        >
                          <ControlesPaginacao
                            page={page}
                            totalPages={totalPages}
                            totalProcesses={totalProcesses}
                            primeiroProcesso={primeiroProcesso}
                            ultimoProcesso={ultimoProcesso}
                            onChange={handleChangePage}
                          />
                        </Box>
                      </div>
                    )}
                  </>
                )}
              </Paper>
            </Box>
          </Container>
        </div>
      </Box>
    </TransicaoPagina>
  );
}
